/**
 * ScreenState — the one way a screen renders loading, error, empty and content.
 *
 * WHY THIS EXISTS
 *
 * Two defects made this necessary, both found in the Fase 3/4 audit:
 *
 * 1. Only 4 of 37 query-backed screens rendered an error state at all. When a
 *    query failed, the other 33 left the user on a skeleton that never
 *    resolved, or on a blank screen — a dead end with no way forward. The
 *    brief forbids exactly that ("cero callejones": every error and empty
 *    screen needs a primary action).
 *
 * 2. Screens branched with sibling `return`s, each re-wrapping its own
 *    ScreenContainer. Sibling returns are different JSX positions, so React
 *    remounted the container on every loading -> content transition, losing
 *    scroll position and replaying entrance animations. Same class as the
 *    startup flicker fixed in RootLayoutApp.
 *
 * Both disappear when the container stays hoisted and only the INNER state
 * varies — which is what this component enforces.
 *
 * USAGE
 *
 *   <ScreenContainer scroll={false}>
 *     <ScreenHeader title={t('hosting.my_listings')} />
 *     <ScreenState
 *       query={vehiclesQuery}
 *       skeleton={<ListingsSkeleton />}
 *       empty={{ title: t('hosting.no_vehicles'), action: { label: ..., onPress: ... } }}
 *     >
 *       {(vehicles) => <VehicleList data={vehicles} />}
 *     </ScreenState>
 *   </ScreenContainer>
 *
 * The container and the header render once and stay mounted. Only the region
 * below swaps.
 */

import React from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { WifiOff } from '@/components/ui/icons'
import { EmptyState } from './EmptyState'
import { COLORS } from '../../lib/brand'

/** The slice of a TanStack query this component needs. */
interface ScreenStateQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => unknown
}

interface EmptyConfig {
  title: string
  description?: string
  action?: { label: string; onPress: () => void }
}

interface ScreenStateProps<TData> {
  query: ScreenStateQuery<TData>
  /** Shown while loading. Always a skeleton — never a bare spinner. */
  skeleton: React.ReactNode
  /**
   * Shown when the query resolved with nothing. Omit when `data` cannot be
   * meaningfully empty (a detail screen either has its record or errors).
   */
  empty?: EmptyConfig
  /** Overrides the default error copy. The retry action is always provided. */
  error?: { title?: string; description?: string }
  children: (data: TData) => React.ReactNode
}

/**
 * Both non-content states fill the region they are handed and centre inside it.
 *
 * `EmptyState` centres its own children but has no `flex`, so inside a
 * `ScreenContainer scroll={false}` it collapsed to its content height and
 * stuck to the top: the /trips/[tripId] error was three lines and a button
 * pinned under the status bar with the rest of the screen empty below it.
 */
function StateRegion({ children }: { children: React.ReactNode }) {
  return <View style={{ flex: 1, justifyContent: 'center' }}>{children}</View>
}

function isEmpty(data: unknown): boolean {
  if (data == null) return true
  if (Array.isArray(data)) return data.length === 0
  return false
}

export function ScreenState<TData>({
  query,
  skeleton,
  empty,
  error,
  children,
}: ScreenStateProps<TData>) {
  const { t } = useTranslation()

  if (query.isLoading) return <>{skeleton}</>

  // Error always offers a way out. This is the whole point: a failed query
  // must never leave the user staring at a skeleton with nothing to tap.
  if (query.isError) {
    return (
      <StateRegion>
        <EmptyState
          unavailable
          variant="bloom"
          illustration={<WifiOff size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
          title={error?.title ?? t('common.error_title')}
          description={error?.description ?? t('common.error_description')}
          action={{ label: t('common.retry'), onPress: () => void query.refetch() }}
        />
      </StateRegion>
    )
  }

  if (query.data === undefined) return <>{skeleton}</>

  if (empty && isEmpty(query.data)) {
    return (
      <StateRegion>
        <EmptyState
          variant="bloom"
          title={empty.title}
          description={empty.description}
          action={empty.action}
        />
      </StateRegion>
    )
  }

  return <>{children(query.data)}</>
}
